import {
    AlertModal,
    ArrowLeft,
    ArrowRight,
    Box,
    Button,
    Caption,
    Card,
    Close,
    Divider,
    Heading,
    LogoLoader,
    showToast,
    Status,
    StatusBadge,
    TextBlock,
    ZoomIn,
    ZoomOut,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/plugins/captions.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

import style from "components/asset-management-system/asset-side-panel.scss";
import { formatAssetValue } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/estimated-value-card/estimatedValue";
import {
    GradeBadge,
    NO_GRADE,
} from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/grade-badge/GradeBadge";
import { GradingActionSelect } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/grading-action-select/GradingActionSelect";
import UpliftIcon from "components/icons/UpliftIcon";
import { AssetPathKey } from "domain/asset-management/assetPathMapping";
import { isGrade } from "domain/grade";
import { UpliftRule } from "domain/upliftRules";
import {
    assetGradingService,
    CosmeticGradingImage,
    CosmeticGradingResult,
    CosmeticGradingUserOutcome,
    CosmeticGradingUserOverride,
} from "services/asset-management/AssetGradingService";
import { Asset, assetService } from "services/asset-management/AssetService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    assetUuid: string;
    asset: Asset;
    onAssetUpdated?: (asset: Asset) => void;
}

interface GradeInfo {
    titleKey: string;
    detailsKey: string;
}

interface DevicePhotoTile {
    url: string;
    label: string;
}

const GRADE_INFO: Record<string, GradeInfo> = {
    A: {
        titleKey: "AssetManagement.sidePanel.grading.grade.gradeA.title",
        detailsKey: "AssetManagement.sidePanel.grading.grade.gradeA.details",
    },
    B: {
        titleKey: "AssetManagement.sidePanel.grading.grade.gradeB.title",
        detailsKey: "AssetManagement.sidePanel.grading.grade.gradeB.details",
    },
    C: {
        titleKey: "AssetManagement.sidePanel.grading.grade.gradeC.title",
        detailsKey: "AssetManagement.sidePanel.grading.grade.gradeC.details",
    },
    D: {
        titleKey: "AssetManagement.sidePanel.grading.grade.gradeD.title",
        detailsKey: "AssetManagement.sidePanel.grading.grade.gradeD.details",
    },
};

// The cosmetic grading API returns imageType as FRONT/BACK/SIDE_LEFT/SIDE_RIGHT/SIDE_TOP/SIDE_BOTTOM/SIDE, which
// doesn't match the existing DevicePhotos.positions translation keys (front/back/top/bottom/leftSide/rightSide/side).
const IMAGE_TYPE_TO_POSITION_KEY: Record<string, string> = {
    FRONT: "front",
    BACK: "back",
    SIDE_TOP: "top",
    SIDE_BOTTOM: "bottom",
    SIDE_LEFT: "leftSide",
    SIDE_RIGHT: "rightSide",
    SIDE: "side",
};

const OUTCOME_TO_STATE_KEY: Record<CosmeticGradingUserOutcome, string> = {
    FAILED: "Common.state.failed",
    PASSED: "Common.state.passed",
};

interface UpliftRuleCard {
    rule: UpliftRule;
    stillApplicable: boolean;
}

const sumApplicableUplift = (rules: UpliftRuleCard[]): number =>
    rules.filter((each) => each.stillApplicable).reduce((sum, each) => sum + each.rule.value, 0);
function toDevicePhotoPositionKey(imageType: string): string {
    return IMAGE_TYPE_TO_POSITION_KEY[imageType] ?? imageType;
}

function extractOptimizationSuggestions(asset: Asset): string[] {
    const foundValue = asset.assetDetails?.find((each) => each.key === AssetPathKey.OPTIMIZATION_SUGGESTIONS);
    if (!foundValue) {
        return [];
    }
    try {
        const parsed: unknown = JSON.parse(foundValue.value);
        return Array.isArray(parsed) ? parsed.filter((each): each is string => typeof each === "string") : [];
    } catch (exception) {
        logger.warn("Cannot parse optimization suggestions of asset", exception);
        return [];
    }
}

export const AssetGradingView = (props: Props) => {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(false);
    const [asset, setAsset] = React.useState<Asset>(props.asset);
    const [assetOptimizationSuggestions, setAssetOptimizationSuggestions] = React.useState<string[]>(
        extractOptimizationSuggestions(props.asset)
    );
    const [toShowSaveFailureAlert, setToShowSaveFailureAlert] = React.useState(false);
    const [error, setError] = React.useState<string>();
    const [cosmeticGradingResult, setCosmeticGradingResult] = React.useState<CosmeticGradingResult>();
    const [lightBoxGalleryIndex, setLightBoxGalleryIndex] = React.useState(-1);
    const saveAbortControllerRef = React.useRef<AbortController>();
    const [toRefresh, setToRefresh] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    useEffect(
        () => () => {
            saveAbortControllerRef.current?.abort();
            setIsSaving(false);
        },
        [props.assetUuid]
    );

    const toDevicePhotoImages = (image: CosmeticGradingImage): DevicePhotoTile[] => {
        const positionLabel = t(
            `AssetManagement.sidePanel.grading.DevicePhotos.positions.${toDevicePhotoPositionKey(image.imageType)}`
        );

        // Privacy variant is preferred because originals can show the device screen. Fall back to the original when absent.
        const imageUrl = image.privacyPresignedImageUrl ?? image.presignedImageUrl;
        const isFailed = image.detectedDamageTypes.length > 0;
        // Failed images show the damage variant so the detected damage is visible without opening each photo.
        if (isFailed && image.damagePresignedImageUrl !== undefined) {
            const damageLabel = t("AssetManagement.sidePanel.grading.DevicePhotos.variants.damage");
            return [{ url: image.damagePresignedImageUrl, label: `${positionLabel} - ${damageLabel}` }];
        }
        return imageUrl === undefined ? [] : [{ url: imageUrl, label: positionLabel }];
    };

    const toUserOverrideImageUrl = (userOverride: CosmeticGradingUserOverride): string | undefined => {
        // Originals can show the device screen, so prefer the privacy variant when the overridden image is still the
        // current one. A stale override only has the original, since the newer image has replaced its variants.
        const currentImage = userOverride.isCurrentImage
            ? cosmeticGradingResult?.images.find((image) => image.imageType === userOverride.imageType)
            : undefined;
        return currentImage?.privacyPresignedImageUrl ?? userOverride.presignedImageUrl;
    };

    // The AI result of an image is only known while it is the current one, since a newer image replaces its results.
    const toAiOutcome = (userOverride: CosmeticGradingUserOverride): CosmeticGradingUserOutcome | undefined => {
        const currentImage = userOverride.isCurrentImage
            ? cosmeticGradingResult?.images.find((image) => image.imageType === userOverride.imageType)
            : undefined;
        if (currentImage === undefined) {
            return undefined;
        }
        return currentImage.detectedDamageTypes.length > 0 ? "FAILED" : "PASSED";
    };

    const createOutcomeBadge = (outcome: CosmeticGradingUserOutcome) => (
        <StatusBadge
            value={t(OUTCOME_TO_STATE_KEY[outcome])}
            status={outcome === "FAILED" ? Status.ERROR : Status.SUCCESS}
        />
    );

    // The parent refreshes its asset, for example when the view is refreshed, and it is the newer copy then.
    useEffect(() => {
        setAsset(props.asset);
        setAssetOptimizationSuggestions(extractOptimizationSuggestions(props.asset));
    }, [props.asset]);

    useEffect(() => {
        if (!toRefresh) {
            return;
        }
        setLoading(true);
        setError(undefined);
        const abortController = new AbortController();
        assetService
            .fetchAssetByUuid(props.assetUuid, abortController)
            .then((response) => {
                if (!abortController.signal.aborted) {
                    setAsset(response);
                    setAssetOptimizationSuggestions(extractOptimizationSuggestions(response));
                    props.onAssetUpdated?.(response);
                }
                setToRefresh(false);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setError("Failed to load grading data");
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            });
        return () => abortController.abort();
    }, [toRefresh]);

    useEffect(() => {
        const abortController = new AbortController();
        assetGradingService
            .fetchCosmeticGradingResult(props.assetUuid, abortController)
            .then((response) => {
                if (!abortController.signal.aborted) {
                    setCosmeticGradingResult(response);
                }
            })
            .catch((exception) => {
                if (!abortController.signal.aborted) {
                    logger.warn("Failed to fetch cosmetic grading result", exception.message);
                }
            });
        return () => abortController.abort();
    }, [props.assetUuid]);

    if (loading) {
        return (
            <Box horizontalAlignment="Center" verticalAlignment="Center" className={style.loadingState}>
                <LogoLoader />
            </Box>
        );
    }

    if (error) {
        return (
            <Box columnLayout gap="Xs">
                <TextBlock disableBottomSpacing>{error}</TextBlock>
            </Box>
        );
    }

    if (asset === undefined) {
        return null;
    }

    const assetRecord = asset as Record<
        string,
        string | string[] | boolean | { key: string; value: string }[] | Asset[] | undefined
    >;
    const blanccoGrade = assetRecord.blancco_grade;
    const gradeKey = typeof blanccoGrade === "string" && blanccoGrade ? blanccoGrade.toUpperCase() : undefined;
    const gradeInfo = gradeKey !== undefined ? GRADE_INFO[gradeKey] : undefined;
    const assetValue = formatAssetValue(
        assetRecord[AssetPathKey.ASSET_VALUE],
        assetRecord[AssetPathKey.POTENTIAL_UPLIFT_VALUE]
    );
    if (gradeKey !== undefined && gradeInfo === undefined) {
        return (
            <Box columnLayout gap="Xs">
                <TextBlock disableBottomSpacing>{t("AssetManagement.sidePanel.grading.invalidGrade")}</TextBlock>
            </Box>
        );
    }

    const testResults = Object.entries(cosmeticGradingResult?.testResults ?? {});
    const devicePhotoTiles = (cosmeticGradingResult?.images ?? []).flatMap((image) => toDevicePhotoImages(image));
    const userOverrides = (cosmeticGradingResult?.userOverrides ?? []).map((userOverride) => ({
        userOverride,
        label: t(
            `AssetManagement.sidePanel.grading.DevicePhotos.positions.${toDevicePhotoPositionKey(userOverride.imageType)}`
        ),
        imageUrl: toUserOverrideImageUrl(userOverride),
        aiOutcome: toAiOutcome(userOverride),
    }));
    // The override tiles follow the device photo tiles in the same gallery, so their index is offset by the latter.
    const userOverrideTiles = userOverrides.flatMap((each) =>
        each.imageUrl === undefined
            ? []
            : [
                  {
                      url: each.imageUrl,
                      label: each.userOverride.userComment
                          ? `${each.label}: ${each.userOverride.userComment}`
                          : each.label,
                  },
              ]
    );
    const lightBoxGalleryImages = [...devicePhotoTiles, ...userOverrideTiles].map((galleryTile: DevicePhotoTile) => ({
        src: galleryTile.url,
        description: galleryTile.label,
    }));

    const createGradeCard = () => {
        if (gradeInfo === undefined && assetValue === undefined) {
            return (
                <Box columnLayout gap="Xs">
                    <TextBlock disableBottomSpacing>
                        {t("AssetManagement.sidePanel.grading.noGradeAvailable")}
                    </TextBlock>
                </Box>
            );
        }
        const valueContent = assetValue !== undefined && (
            <Box columnLayout gap="Xxxs" className={style.detailValueRight}>
                <Heading variant="SUBTITLE_2" disableTopSpacing disableBottomSpacing>
                    {assetValue}
                </Heading>
                <TextBlock disableBottomSpacing className={style.gradingDetails}>
                    {t("AssetManagement.sidePanel.grading.estimatedValue")}
                </TextBlock>
            </Box>
        );
        return (
            <GradeBadge
                grade={isGrade(gradeKey) ? gradeKey : NO_GRADE}
                size={48}
                withBackground
                title={gradeInfo ? t(gradeInfo.titleKey) : t("AssetManagement.sidePanel.grading.noGradeAvailable")}
                description={gradeInfo ? t(gradeInfo.detailsKey) : undefined}
                trailingContent={valueContent}
            />
        );
    };

    const saveOptimizationSuggestion = (suggestionUuid: string) => {
        if (isSaving) {
            return;
        }
        const updatedSuggestions = assetOptimizationSuggestions.includes(suggestionUuid)
            ? assetOptimizationSuggestions.filter((each) => each !== suggestionUuid)
            : [...assetOptimizationSuggestions, suggestionUuid];
        const difference = {
            key_before: AssetPathKey.OPTIMIZATION_SUGGESTIONS,
            value_before: JSON.stringify(assetOptimizationSuggestions),
            key_after: AssetPathKey.OPTIMIZATION_SUGGESTIONS,
            value_after: JSON.stringify(updatedSuggestions),
        };
        const abortController = new AbortController();
        saveAbortControllerRef.current = abortController;
        setIsSaving(true);
        assetService
            .editAssetWithGivenDetails(asset.uuid, difference, abortController)
            .then(() => {
                if (abortController.signal.aborted) {
                    return;
                }
                showToast(t("AssetManagement.sidePanel.grading.OptimizationSuggestions.editSucceeded"), "success");
                setAssetOptimizationSuggestions(updatedSuggestions);
                setToRefresh(true);
            })
            .catch((error) => {
                if (abortController.signal.aborted) {
                    return;
                }
                logger.error("Cannot save asset's optimization suggestion.", error);
                setToShowSaveFailureAlert(true);
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setIsSaving(false);
                }
            });
    };

    const createRuleCard = (cardRule: UpliftRuleCard) => {
        const rule = cardRule.rule;
        return (
            <Card
                key={rule.uuid}
                onClick={() => {
                    saveOptimizationSuggestion(rule.uuid);
                }}
                testId={testIds.workArea.asset.sidePanel.gradingTab.optimizationSuggestions.card}
                dataType={rule.uuid}
                className={classNames({
                    [style.warningBorderColor]:
                        !cardRule.stillApplicable && assetOptimizationSuggestions.includes(rule.uuid),
                    [style.cardBorderColor]:
                        assetOptimizationSuggestions.includes(rule.uuid) && cardRule.stillApplicable,
                })}
            >
                <Box justifyContent={"SpaceBetween"}>
                    <Box columnLayout={true}>
                        <Heading variant={"SUBTITLE_2"} disableBottomSpacing={true} disableTopSpacing={true}>
                            {cardRule.stillApplicable
                                ? rule.headline
                                : t(
                                      "AssetManagement.sidePanel.grading.OptimizationSuggestions.ruleNotApplicableAnymore",
                                      { ruleName: rule.headline }
                                  )}
                        </Heading>
                        <Caption greyTextStyle={true}>{rule.description}</Caption>
                    </Box>
                    <Box columnLayout={true}>
                        <Heading variant={"SUBTITLE_2"} disableBottomSpacing={true} disableTopSpacing={true}>
                            +{formatAssetValue(rule.value)}
                        </Heading>
                        <Caption greyTextStyle={true}>
                            {t("AssetManagement.sidePanel.grading.OptimizationSuggestions.uplift")}
                        </Caption>
                    </Box>
                </Box>
            </Card>
        );
    };

    const createOptimizationSuggestionsCard = () => {
        if (!assetValue) {
            return <></>;
        }
        const allRules: UpliftRuleCard[] = [];

        if (asset.potentialUpliftRules && asset.potentialUpliftRules.length > 0) {
            asset.potentialUpliftRules.forEach((each) => {
                allRules.push({
                    rule: each,
                    stillApplicable: true,
                });
            });
        }
        const potentialRuleUuids = allRules.map((each) => each.rule.uuid);
        if (asset.appliedUpliftRules && asset.appliedUpliftRules.length > 0) {
            asset.appliedUpliftRules.forEach((each) => {
                if (!potentialRuleUuids.includes(each.uuid)) {
                    allRules.push({ rule: each, stillApplicable: false });
                }
            });
        }
        if (allRules.length < 1) {
            return <></>;
        }
        const cards = allRules.map((each) => {
            return createRuleCard(each);
        });
        return (
            <>
                <Divider direction={"Horizontal"} />
                <Box columnLayout gap={"Xxs"}>
                    <Box
                        justifyContent={"SpaceBetween"}
                        verticalAlignment={"Center"}
                        testId={testIds.workArea.asset.sidePanel.gradingTab.optimizationSuggestions.itself}
                    >
                        <Heading variant={"SUBTITLE_1"} disableBottomSpacing={true} disableTopSpacing={true}>
                            {t("AssetManagement.sidePanel.grading.OptimizationSuggestions.title")}
                        </Heading>
                        <Box
                            justifyContent={"SpaceBetween"}
                            verticalAlignment={"Center"}
                            className={style.potentialUpliftCard}
                        >
                            <Box marginRight={"Xxs"}>
                                <UpliftIcon />
                            </Box>
                            <TextBlock
                                disableBottomSpacing
                                testId={
                                    testIds.workArea.asset.sidePanel.gradingTab.optimizationSuggestions
                                        .potentialUpliftLabel
                                }
                            >
                                {t("AssetManagement.sidePanel.grading.OptimizationSuggestions.potentialUplift", {
                                    upliftValue: formatAssetValue(sumApplicableUplift(allRules)),
                                })}
                            </TextBlock>
                        </Box>
                    </Box>
                    <TextBlock disableBottomSpacing>
                        {t("AssetManagement.sidePanel.grading.OptimizationSuggestions.subtitle")}
                    </TextBlock>
                </Box>
                {cards}
            </>
        );
    };

    return (
        <Box columnLayout gap={"Md"}>
            <Box columnLayout gap="Sm">
                {createGradeCard()}
                {gradeInfo !== undefined && (
                    <Box gap="Sm" verticalAlignment="Top">
                        <span className={style.detailLabel}>
                            {t("AssetManagement.value.gradingTable.actionHeader")}
                        </span>
                        <Box columnLayout gap="Xxs" className={style.gradingActionField}>
                            <GradingActionSelect
                                key={props.assetUuid}
                                asset={asset}
                                testId={testIds.workArea.asset.sidePanel.gradingTab.actionSelect}
                            />
                            <TextBlock disableBottomSpacing className={style.gradingActionHelperText}>
                                {t("AssetManagement.sidePanel.grading.actionDescription")}
                            </TextBlock>
                        </Box>
                    </Box>
                )}
            </Box>
            {createOptimizationSuggestionsCard()}
            <Divider direction={"Horizontal"} />
            <Box columnLayout gap={"Xs"}>
                <Heading variant={"SUBTITLE_1"} disableBottomSpacing disableTopSpacing>
                    {t("AssetManagement.sidePanel.grading.AiCosmetic.title")}
                </Heading>
                {testResults.length === 0 ? (
                    <TextBlock disableBottomSpacing>
                        {t("AssetManagement.sidePanel.grading.AiCosmetic.noIssuesDetected")}
                    </TextBlock>
                ) : (
                    testResults.map(([testName, result]) => (
                        <Box key={testName} gap={"Xxs"} justifyContent={"SpaceBetween"}>
                            <TextBlock disableBottomSpacing className={style.detailLabel}>
                                {t(`DiagnosticReportsTable.diagnosticSummary.tests.${testName}`)}
                            </TextBlock>
                            <TextBlock
                                disableBottomSpacing
                                className={classNames(style.detailValue, style.detailValueRight)}
                            >
                                {t(
                                    result
                                        ? "AssetManagement.sidePanel.grading.AiCosmetic.detected"
                                        : "AssetManagement.sidePanel.grading.AiCosmetic.notDetected"
                                )}
                            </TextBlock>
                        </Box>
                    ))
                )}
            </Box>
            <Divider direction={"Horizontal"} />
            <Box columnLayout gap={"Xs"}>
                <Box justifyContent={"SpaceBetween"}>
                    <Heading disableBottomSpacing disableTopSpacing variant={"SUBTITLE_1"}>
                        {t("AssetManagement.sidePanel.grading.DevicePhotos.title")}
                    </Heading>
                    <TextBlock disableBottomSpacing>
                        {t("AssetManagement.sidePanel.grading.DevicePhotos.totalImages", {
                            totalImages: devicePhotoTiles.length,
                        })}
                    </TextBlock>
                </Box>
                <TextBlock disableBottomSpacing>
                    {t("AssetManagement.sidePanel.grading.DevicePhotos.description")}
                </TextBlock>
                <div className={style.gradingPhotoWrapper}>
                    {devicePhotoTiles.map((image, index) => (
                        <Box
                            key={index}
                            columnLayout
                            gap={"Xxxs"}
                            verticalAlignment={"Center"}
                            onClick={() => setLightBoxGalleryIndex(index)}
                        >
                            <img src={image.url} alt={image.label} loading="lazy" />
                            <TextBlock disableBottomSpacing>{image.label}</TextBlock>
                        </Box>
                    ))}
                </div>
                {userOverrides.length > 0 && (
                    <>
                        <Divider direction={"Horizontal"} />
                        <Box columnLayout gap={"Xs"}>
                            <Heading variant={"SUBTITLE_1"} disableBottomSpacing disableTopSpacing>
                                {t("AssetManagement.sidePanel.grading.UserOverrides.title")}
                            </Heading>
                            <TextBlock disableBottomSpacing>
                                {t("AssetManagement.sidePanel.grading.UserOverrides.description")}
                            </TextBlock>
                            <div className={style.gradingUserOverrideWrapper}>
                                {userOverrides.map(({ userOverride, label, imageUrl, aiOutcome }) => {
                                    const tileIndex = userOverrideTiles.findIndex((tile) => tile.url === imageUrl);
                                    return (
                                        <Box
                                            key={userOverride.imageType}
                                            columnLayout
                                            gap={"Xxxs"}
                                            onClick={
                                                imageUrl === undefined
                                                    ? undefined
                                                    : () => setLightBoxGalleryIndex(devicePhotoTiles.length + tileIndex)
                                            }
                                        >
                                            {imageUrl !== undefined && (
                                                <img src={imageUrl} alt={label} loading="lazy" />
                                            )}
                                            <TextBlock disableBottomSpacing className={style.userOverrideTitle}>
                                                {label}
                                            </TextBlock>
                                            <Box justifyContent={"SpaceBetween"} verticalAlignment={"Center"}>
                                                <TextBlock disableBottomSpacing className={style.userOverrideLabel}>
                                                    {t("AssetManagement.sidePanel.grading.UserOverrides.aiResult")}
                                                </TextBlock>
                                                {aiOutcome === undefined ? (
                                                    <Caption greyTextStyle={true}>
                                                        {t(
                                                            "AssetManagement.sidePanel.grading.UserOverrides.notAvailable"
                                                        )}
                                                    </Caption>
                                                ) : (
                                                    createOutcomeBadge(aiOutcome)
                                                )}
                                            </Box>
                                            <Box justifyContent={"SpaceBetween"} verticalAlignment={"Center"}>
                                                <TextBlock disableBottomSpacing className={style.userOverrideLabel}>
                                                    {t("AssetManagement.sidePanel.grading.UserOverrides.userResult")}
                                                </TextBlock>
                                                {createOutcomeBadge(userOverride.userOutcome)}
                                            </Box>
                                            {userOverride.userComment !== undefined && (
                                                <Box columnLayout gap={"Xxxs"}>
                                                    <TextBlock disableBottomSpacing className={style.userOverrideLabel}>
                                                        {t("AssetManagement.sidePanel.grading.UserOverrides.comment")}
                                                    </TextBlock>
                                                    <TextBlock
                                                        disableBottomSpacing
                                                        className={style.userOverrideComment}
                                                    >
                                                        {userOverride.userComment}
                                                    </TextBlock>
                                                </Box>
                                            )}
                                            {!userOverride.isCurrentImage && (
                                                <Caption greyTextStyle={true}>
                                                    {t("AssetManagement.sidePanel.grading.UserOverrides.earlierImage")}
                                                </Caption>
                                            )}
                                        </Box>
                                    );
                                })}
                            </div>
                        </Box>
                    </>
                )}

                <Lightbox
                    index={lightBoxGalleryIndex}
                    plugins={[Zoom, Captions]}
                    slides={lightBoxGalleryImages}
                    open={lightBoxGalleryIndex >= 0}
                    close={() => setLightBoxGalleryIndex(-1)}
                    render={{
                        iconPrev: () => <ArrowLeft size={32} color={"--theme-icon-fill-white"} />,
                        iconNext: () => <ArrowRight size={32} color={"--theme-icon-fill-white"} />,
                        iconClose: () => <Close size={32} color={"--theme-icon-fill-white"} />,
                        iconZoomIn: () => <ZoomIn size={32} color={"--theme-icon-fill-white"} />,
                        iconZoomOut: () => <ZoomOut size={32} color={"--theme-icon-fill-white"} />,
                    }}
                    portal={{
                        container: { onPointerDown: (event) => event.stopPropagation() },
                    }}
                    captions={{
                        descriptionTextAlign: "center",
                    }}
                    className={style.lightbox}
                />
                <AlertModal
                    title={t("AssetManagement.sidePanel.grading.OptimizationSuggestions.editFailed.title")}
                    variant={"Error"}
                    isOpen={toShowSaveFailureAlert}
                    hideModal={() => setToShowSaveFailureAlert(false)}
                    buttons={
                        <Button
                            variant={"Primary"}
                            testId={testIds.common.dialog.closeButton}
                            onClick={() => setToShowSaveFailureAlert(false)}
                        >
                            {t("Common.ok")}
                        </Button>
                    }
                >
                    <TextBlock>
                        {t("AssetManagement.sidePanel.grading.OptimizationSuggestions.editFailed.message")}
                    </TextBlock>
                </AlertModal>
            </Box>
        </Box>
    );
};
